import { CSSProperties } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { trackSegment } from '~/components/SegmentAnalytics';
import { getEnvVariable } from '~/dev/utils/getEnvVariable';
import { Menu } from '~/eds/src/components/Menu/Menu';
import { AskAnythingButton } from '~/features/ask-anything/ask-anything-button/AskAnythingButton';
import { NotificationTray } from '~/features/notifications/NotificationTray';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { usePermissions } from '~/hooks/usePermissions';
import { useSearchModal } from '~/hooks/useSearchModal';
import { Icon } from '~/packages/eds/src/components/Icon/Icon';
import { Tooltip } from '~/packages/eds/src/components/Tooltip/Tooltip';
import { RbacPermission } from '~/types/Rbac';

import { UserProfileMenu } from './UserProfileMenu';
import './AppHeaderActions.css';

const messages = defineMessages({
  searchAriaLabel: {
    id: 'AppHeaderActions.searchAriaLabel',
    defaultMessage: 'Search',
    description: 'Accessible label for the search icon button in the header.',
  },
  helpLabel: {
    id: 'AppHeaderActions.helpLabel',
    defaultMessage: 'Help',
    description:
      'Label for the help button (used as both tooltip text and accessible label).',
  },
});

const COLORS = {
  iconMuted: 'var(--eds--color--fg-hint)',
};

const containerStyles: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexShrink: 0,
};

const iconBtnStyles: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: 6,
  border: 'none',
  cursor: 'pointer',
  color: COLORS.iconMuted,
  flexShrink: 0,
};

const ASK_AI_PERMISSION: RbacPermission[] = [
  { resourceId: 'conversational_ai', resourceType: 'edit' },
];

export const AppHeaderActions = () => {
  const intl = useIntl();

  const hasAskAnything = useFlag(FlagType.AskAnything);
  const enableCandu = useFlag(FlagType.CanduScript);
  const hasSidebarNav = useFlag(FlagType.SidebarNav);

  const { checkPermission } = usePermissions({
    permissions: ASK_AI_PERMISSION,
  });
  const hasAskAnythingPermission = checkPermission('conversational_ai', 'edit');

  const [searchModal, setSearchModal] = useSearchModal();

  const onSearchClick = () => {
    trackSegment('selectNavigation', { name: 'Quick Search' });
    setSearchModal();
  };

  const helpOptions = enableCandu
    ? []
    : [
        {
          label: intl.formatMessage({
            id: 'PageHeader.knowledgeCenter.',
            defaultMessage: 'Knowledge Center',
            description: 'Label for the knowledge center menu action',
          }),
          href:
            getEnvVariable('ZENDESK_AUTH_ENTRY_URL') ||
            'https://support.evisort.com/hc/en-us',
          value: 'knowledge-center',
        },
        {
          label: intl.formatMessage({
            id: 'PageHeader.evisortAcademy',
            defaultMessage: 'Evisort Academy',
            description: 'Label for the Evisort Academy menu action',
          }),
          href: 'https://academy.evisort.com/',
          value: 'evisort-academy',
        },
      ];

  return (
    <>
      <div style={containerStyles}>
        {hasAskAnything && hasAskAnythingPermission && !hasSidebarNav && (
          <AskAnythingButton />
        )}

        <NotificationTray appearance="header" />

        <Tooltip
          tooltip={intl.formatMessage({
            id: 'PageHeader.searchText',
            defaultMessage: 'Search',
            description: 'Text label for the search button',
          })}
          direction="bottom"
        >
          <button
            type="button"
            aria-label={intl.formatMessage(messages.searchAriaLabel)}
            className="header-icon-btn"
            style={iconBtnStyles}
            onClick={onSearchClick}
          >
            <Icon icon="search" size="s" />
          </button>
        </Tooltip>

        {helpOptions.length > 0 ? (
          <Menu
            name={intl.formatMessage({
              id: 'PageHeader.helpMenuName',
              defaultMessage: 'help menu',
              description: 'Accessible name for the help menu',
            })}
            options={helpOptions}
            direction="bottom"
            enablePortal
            trigger={
              <button
                type="button"
                aria-label={intl.formatMessage(messages.helpLabel)}
                className="header-icon-btn"
                style={iconBtnStyles}
              >
                <Icon icon="help" size="s" />
              </button>
            }
          />
        ) : (
          <Tooltip
            tooltip={intl.formatMessage(messages.helpLabel)}
            direction="bottom"
          >
            <button
              type="button"
              aria-label={intl.formatMessage(messages.helpLabel)}
              className="header-icon-btn"
              style={iconBtnStyles}
            >
              <Icon icon="help" size="s" />
            </button>
          </Tooltip>
        )}

        <UserProfileMenu />
      </div>
      {searchModal}
    </>
  );
};
